import { motion } from "framer-motion";
import { Smartphone, Brain, Target, Layers, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Animation variants
const cardVariants = {
  hidden: { opacity: 0, y: 35 },
  visible: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay },
  }),
};

// Expertise Card Component
const ExpertiseCard = ({ icon: Icon, title, description, tags, highlights, businessValue, className, delay, image }) => {
  return (
    <motion.div
      variants={cardVariants}
      custom={delay}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className={`glass-card-premium p-8 lg:p-10 flex flex-col relative overflow-hidden group ${className}`}
      data-testid={`expertise-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Background Image with premium treatment */}
      {image && (
        <div className="absolute inset-0 z-0">
          <img 
            src={image} 
            alt="" 
            className="w-full h-full object-cover opacity-[0.15] group-hover:opacity-[0.22] transition-opacity duration-700 scale-105 group-hover:scale-100" 
            loading="lazy" 
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1E] via-[#0A0F1E]/85 to-[#0A0F1E]/60" />
          <div className="absolute inset-0 bg-gradient-to-br from-lenvia-blue/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        </div>
      )}

      <div className="relative z-10 flex flex-col h-full">
        {/* Icon */}
        <div className="icon-container mb-7 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-6 h-6 text-lenvia-blue" />
        </div>
        
        {/* Title & Description */}
        <h3 className="font-heading text-2xl font-bold text-white mb-4 leading-tight">{title}</h3>
        <p className="text-slate-300 leading-relaxed mb-7 text-base">{description}</p>
        
        {/* Tags */}
        {tags && (
          <div className="flex flex-wrap gap-2.5 mb-7">
            {tags.map((tag) => (
              <span 
                key={tag} 
                className="text-xs px-3.5 py-2 bg-white/[0.05] rounded-full text-slate-200 border border-white/[0.08] font-medium hover:bg-white/[0.08] transition-colors"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Highlights */}
        {highlights && (
          <ul className="space-y-3 mb-7">
            {highlights.map((highlight) => (
              <li key={highlight} className="flex items-start gap-3 text-sm">
                <span className="w-2 h-2 rounded-full bg-lenvia-blue/70 mt-1.5 flex-shrink-0" />
                <span className="text-slate-200">{highlight}</span>
              </li>
            ))}
          </ul>
        )}
        
        {/* Business Value */}
        {businessValue && (
          <div className="mt-auto pt-6 border-t border-white/[0.08]">
            <p className="text-sm font-semibold text-lenvia-blue flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4" />
              {businessValue}
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Expertises Section
const ExpertisesSection = () => {
  return (
    <section className="section-spacing relative noise-overlay section-elevated">
      <div id="expertises" className="lenvia-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-20"
        >
          <span className="inline-flex items-center gap-2.5 mb-6">
            <Target className="w-4 h-4 text-lenvia-blue" />
            <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
              Nos Expertises
            </span>
          </span>
          
          <h2 
            className="font-heading text-4xl md:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-white mb-6 leading-[1.08]" 
            data-testid="expertises-title"
          >
            Un savoir-faire
            <br />
            <span className="gradient-text">technologique ciblé</span>
          </h2>
          
          <p className="text-lg lg:text-xl text-slate-300 max-w-2xl leading-relaxed">
            Des expertises conçues pour sécuriser vos systèmes critiques. Concentration sur les sujets à forte valeur ajoutée.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="bento-grid">
          <ExpertiseCard
            icon={Smartphone}
            title="Mobile Engineering"
            description="Conception et développement d'applications natives et multiplateformes robustes pour vos écosystèmes critiques."
            tags={["iOS", "Android", "KMP", "Haute Performance"]}
            highlights={[
              "Applications critiques à forte volumétrie",
              "Performance, stabilité, scalabilité",
              "Réduction du time-to-market",
            ]}
            businessValue="Réduction du time-to-market"
            className="md:col-span-2"
            delay={0}
            image="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=800&q=70"
          />

          <ExpertiseCard
            icon={Brain}
            title="Intelligence Artificielle"
            description="Intégration de solutions LLM et RAG sur-mesure pour automatiser et enrichir vos processus métiers."
            tags={["LLM", "RAG", "Automatisation"]}
            highlights={[
              "Intégration LLM sécurisée",
              "Automatisation de processus",
              "Gains mesurables",
            ]}
            businessValue="Gains de productivité"
            className="md:col-span-1"
            delay={0.1}
            image="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=70"
          />

          <ExpertiseCard
            icon={Layers}
            title="Delivery & Architecture"
            description="Pilotage rigoureux et mise en place d'architectures résilientes pour garantir la réussite technique de vos projets."
            tags={["CI/CD", "Observabilité", "Clean Architecture", "Quality Assurance"]}
            businessValue="Déploiements fiabilisés et sans couture"
            className="md:col-span-3"
            delay={0.2}
            image="https://images.unsplash.com/photo-1663342180037-7fe2e2abc7a4?w=1200&q=70"
          />
        </div>

        {/* Reassurance Block */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 glass-card-subtle p-7 flex flex-col md:flex-row items-center justify-between gap-4"
        >
          <p className="text-slate-300 font-medium text-center md:text-left text-base">
            Une expertise déployée sur des systèmes critiques à forte volumétrie, soumis à des exigences de sécurité et de conformité élevées.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-20 text-center"
        >
          <Button 
            className="btn-primary text-base group px-10" 
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
          >
            Échanger sur votre projet
            <ChevronRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpertisesSection;
